import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, money, imgUrl } from "../../lib/api";
import VideoEmbed from "../../components/store/VideoEmbed";
import CreatorChip from "../../components/store/CreatorChip";
import { ShoppingBag, X, Instagram, Youtube, Heart, ArrowLeft, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/BrandingContext";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "youtube", label: "YouTube Shorts" },
  { key: "instagram", label: "Instagram" },
  { key: "followed", label: "Following", requiresAuth: true },
];

const PAGE_SIZE = 12;

export default function StyleFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [activeId, setActiveId] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const viewedRef = useRef(new Set());
  const sentinelRef = useRef(null);
  const { addToCart, toggleWishlist, inWishlist } = useCart();
  const { user } = useAuth();

  const fetchPage = useCallback(async (platformFilter, pageNum, append) => {
    const params = { limit: PAGE_SIZE, page: pageNum };
    if (platformFilter === "followed") {
      params.followed = true;
    } else if (platformFilter !== "all") {
      params.platform = platformFilter;
    }
    const { data } = await api.get("/social/feed", { params });
    return data || { items: [], total: 0 };
  }, []);

  const loadFirst = useCallback(async (platformFilter) => {
    setLoading(true);
    setError("");
    setPage(1);
    try {
      const data = await fetchPage(platformFilter, 1, false);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load the feed");
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore) return;
    if (items.length >= total) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const data = await fetchPage(filter, next, true);
      setItems((prev) => [...prev, ...(data.items || [])]);
      setPage(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load more reels");
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, filter, items.length, loading, loadingMore, page, total]);

  const { branding } = useBranding();

  useEffect(() => {
    document.title = `Style Feed · ${branding.brand_name}`;
    return () => { document.title = branding.brand_name; };
  }, [branding.brand_name]);

  useEffect(() => { loadFirst(filter); }, [filter, loadFirst]);

  // Infinite scroll — IntersectionObserver watches sentinel at bottom
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "400px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [loadMore]);

  // Deep-link — open a specific reel when ?content=<id>
  useEffect(() => {
    const id = searchParams.get("content");
    if (id) setActiveId(id);
  }, [searchParams]);

  const activeItem = useMemo(() => items.find((i) => i.id === activeId) || null, [items, activeId]);

  const openPlayer = (c) => {
    setActiveId(c.id);
    setSearchParams({ content: c.id }, { replace: true });
    if (!viewedRef.current.has(c.id)) {
      viewedRef.current.add(c.id);
      api.post(`/social/content/${c.id}/interaction`, { action_type: "click" }).catch(() => {});
    }
  };

  const closePlayer = () => {
    setActiveId(null);
    const next = new URLSearchParams(searchParams);
    next.delete("content");
    setSearchParams(next, { replace: true });
  };

  const shareFeed = async () => {
    const url = window.location.origin + "/reels" + (activeId ? `?content=${activeId}` : "");
    try {
      if (navigator.share) {
        await navigator.share({ title: "Style Feed · KalkiBazaar", url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      /* user cancelled */
    }
  };

  const onShopProduct = async (content, product) => {
    // Log product_click best-effort
    api.post(`/social/content/${content.id}/interaction`, {
      action_type: "product_click",
      product_id: product.id,
    }).catch(() => {});
  };

  const onQuickAdd = async (content, product) => {
    try {
      await addToCart(product.id, null, 1);
      toast.success(`Added to bag — ${product.title}`);
      onShopProduct(content, product);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to bag");
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--kb-surface))]" data-testid="style-feed-page">
      {/* Header */}
      <div className="px-6 md:px-12 lg:px-24 pt-12 pb-6 border-b border-[hsl(var(--kb-border))]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline mb-2">KalkiBazaar Reels</div>
            <h1 className="font-display text-4xl lg:text-5xl">Style Feed</h1>
            <p className="text-sm text-[hsl(var(--kb-muted))] mt-3 max-w-xl">
              Short reels from our ateliers, stylists and the community. Tap any reel to shop the look directly.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={shareFeed}
              className="kb-btn-outline text-xs"
              data-testid="feed-share-btn"
            >
              <Share2 size={14} className="mr-2" /> Share
            </button>
          </div>
        </div>

        <div className="mt-6 flex gap-2 flex-wrap">
          {FILTERS.map((f) => {
            const disabled = f.requiresAuth && !user;
            return (
              <button
                key={f.key}
                onClick={() => {
                  if (disabled) {
                    toast.error("Sign in to see creators you follow");
                    return;
                  }
                  setFilter(f.key);
                }}
                data-testid={`feed-filter-${f.key}`}
                className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                  filter === f.key
                    ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]"
                    : disabled
                      ? "bg-white text-[hsl(var(--kb-muted))] border-[hsl(var(--kb-border))] opacity-60"
                      : "bg-white text-[hsl(var(--kb-text))] border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid */}
      <div className="px-6 md:px-12 lg:px-24 py-10">
        {error && (
          <div className="mb-6 border border-red-300 bg-red-50 text-red-800 p-3 text-sm">{error}</div>
        )}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="aspect-[9/16] bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(var(--kb-surface-alt))] mb-4">
              <Instagram size={28} />
            </div>
            <h2 className="font-display text-2xl mb-2">No reels yet</h2>
            <p className="text-sm text-[hsl(var(--kb-muted))]">Come back soon — we're curating a fresh drop.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {items.map((c) => (
              <button
                key={c.id}
                onClick={() => openPlayer(c)}
                className="text-left block group"
                data-testid={`feed-card-${c.id}`}
              >
                <div className="relative">
                  <VideoEmbed content={c} aspect="9x16" testid={`grid-video-${c.id}`} />
                </div>
                <div className="mt-3">
                  <div className="font-medium text-sm line-clamp-2">{c.title}</div>
                  {Array.isArray(c.linked_products) && c.linked_products.length > 0 && (
                    <div className="text-xs text-[hsl(var(--kb-muted))] mt-1 flex items-center gap-1">
                      <ShoppingBag size={11} /> {c.linked_products.length} product{c.linked_products.length > 1 ? "s" : ""}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Infinite-scroll sentinel + manual Load-more fallback */}
        {!loading && items.length > 0 && items.length < total && (
          <>
            <div ref={sentinelRef} aria-hidden="true" className="h-10" />
            <div className="flex justify-center mt-4">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="kb-btn-outline text-xs"
                data-testid="feed-load-more-btn"
              >
                {loadingMore ? "Loading…" : `Load more reels (${total - items.length} more)`}
              </button>
            </div>
          </>
        )}
        {!loading && items.length > 0 && items.length >= total && total > PAGE_SIZE && (
          <div className="text-center text-xs text-[hsl(var(--kb-muted))] mt-6 uppercase tracking-widest">
            — you've reached the end —
          </div>
        )}
      </div>

      {/* Player modal */}
      {activeItem && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-stretch md:items-center justify-center p-0 md:p-6"
          onClick={closePlayer}
          data-testid="feed-player-modal"
        >
          <div
            className="bg-white w-full max-w-5xl max-h-full md:max-h-[92vh] overflow-hidden flex flex-col md:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Video side */}
            <div className="relative bg-black md:w-[45%] flex items-center justify-center">
              <button
                onClick={closePlayer}
                className="absolute left-3 md:hidden z-10 bg-black/50 text-white p-2 rounded-full"
                style={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
                data-testid="feed-player-back-mobile"
                aria-label="Back"
              >
                <ArrowLeft size={18} />
              </button>
              <VideoEmbed content={activeItem} autoPlay aspect="9x16" testid="feed-player-video" />
            </div>

            {/* Info + Shop side */}
            <div className="md:w-[55%] flex flex-col overflow-hidden">
              <div className="p-6 border-b border-[hsl(var(--kb-border))] flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 overline mb-1">
                    {activeItem.platform === "instagram" ? (
                      <Instagram size={12} />
                    ) : (
                      <Youtube size={12} />
                    )}
                    {activeItem.platform === "instagram" ? "Instagram Reel" : "YouTube Short"}
                  </div>
                  <h2 className="font-display text-2xl leading-tight">{activeItem.title}</h2>
                  {activeItem.creator && (
                    <div className="mt-3">
                      <CreatorChip creator={activeItem.creator} showFollow />
                    </div>
                  )}
                  {activeItem.description && (
                    <p className="text-sm text-[hsl(var(--kb-muted))] mt-3 leading-relaxed">
                      {activeItem.description}
                    </p>
                  )}
                  {activeItem.show_follow_button && activeItem.social_handle_url && (
                    <a
                      href={activeItem.social_handle_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-3 text-xs uppercase tracking-widest underline"
                      data-testid="feed-player-follow"
                    >
                      Follow us on {activeItem.platform}
                    </a>
                  )}
                </div>
                <button
                  onClick={closePlayer}
                  className="text-2xl leading-none text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-text))] hidden md:block"
                  aria-label="Close"
                  data-testid="feed-player-close"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Shop this look */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="overline mb-3">Shop this look</div>
                {!(activeItem.linked_products || []).length ? (
                  <div className="text-sm text-[hsl(var(--kb-muted))] py-8 text-center">
                    No products tagged in this reel yet.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activeItem.linked_products.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center gap-3 border border-[hsl(var(--kb-border))] p-3"
                        data-testid={`feed-player-product-${p.id}`}
                      >
                        <Link
                          to={`/product/${p.slug}`}
                          onClick={() => onShopProduct(activeItem, p)}
                          className="shrink-0"
                        >
                          {p.image ? (
                            <img src={imgUrl(p.image)} alt={p.title} className="w-20 h-20 object-cover" loading="lazy" />
                          ) : (
                            <div className="w-20 h-20 bg-[hsl(var(--kb-surface-alt))]" />
                          )}
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/product/${p.slug}`}
                            onClick={() => onShopProduct(activeItem, p)}
                            className="block font-medium text-sm line-clamp-2 hover:underline"
                          >
                            {p.title}
                          </Link>
                          <div className="flex items-baseline gap-2 mt-1">
                            <span className="font-medium text-sm">{money(p.price)}</span>
                            {p.mrp && p.mrp > p.price && (
                              <span className="text-xs text-[hsl(var(--kb-muted))] line-through">{money(p.mrp)}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            onClick={() => onQuickAdd(activeItem, p)}
                            className="kb-btn-primary !text-[11px] !px-3 !py-2 whitespace-nowrap"
                            data-testid={`feed-player-add-${p.id}`}
                          >
                            Add
                          </button>
                          <button
                            onClick={() => toggleWishlist(p.id)}
                            className={`text-[11px] px-3 py-1.5 border border-[hsl(var(--kb-border))] flex items-center justify-center gap-1 ${
                              inWishlist?.(p.id) ? "bg-[hsl(var(--kb-accent))/0.15]" : ""
                            }`}
                            data-testid={`feed-player-wishlist-${p.id}`}
                            aria-label="Add to wishlist"
                          >
                            <Heart size={12} fill={inWishlist?.(p.id) ? "currentColor" : "none"} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {Array.isArray(activeItem.tags) && activeItem.tags.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-[hsl(var(--kb-border))] flex flex-wrap gap-2">
                    {activeItem.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[11px] uppercase tracking-widest px-2 py-1 bg-[hsl(var(--kb-surface-alt))]"
                      >
                        #{t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
